import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { ConfigProvider, theme } from 'antd';
import { StyleProvider, createCache } from '@ant-design/cssinjs';
import zhCN from 'antd/locale/zh_CN';

interface MountOptions {
  el: HTMLElement;
  component: React.ComponentType<any>;
  props?: Record<string, any>;
  customTheme?: 'light' | 'dark';
  /** 强制将样式注入到主文档而非 iframe（用于 traits 面板等主文档组件） */
  forMainDocument?: boolean;
  /** 
   * 跳过卸载旧 Root (仅用于强制重置 DOM 等特殊场景)
   * 警告：可能导致 React Root 泄漏，仅在明确知晓 DOM 已被破坏时使用
   */
  skipUnmount?: boolean;
}

// 全局 Root 映射，支持组件卸载
const rootMap = new WeakMap<HTMLElement, Root>();
// CSS Cache 映射，按 iframe document 缓存
const cacheMap = new WeakMap<Document, ReturnType<typeof createCache>>();

// 标记是否已初始化
let initialized = false;

/**
 * 初始化全局配置
 */
export const initReactMount = () => {
  if (initialized) return;
  initialized = true;
};

/**
 * 获取或创建 CSS cache
 */
const getOrCreateCache = (doc: Document) => {
  let cache = cacheMap.get(doc);
  if (!cache) {
    cache = createCache();
    cacheMap.set(doc, cache);
  }
  return cache;
};

/**
 * 清除指定 Document 的 CSS cache
 * 在 iframe 销毁时调用此函数，确保新 iframe 能够重新生成样式
 */
export const clearCssCache = (doc?: Document) => {
  if (doc) {
    cacheMap.delete(doc);
  }
};

/**
 * 挂载 React 组件到指定 DOM 元素
 * 用于在 GrapesJS Canvas 中动态渲染 React 组件
 * 
 * 使用 StyleProvider 的 container 属性将 CSS-in-JS 样式注入到 iframe 的 head 中
 */
export const mountReactComponent = async ({
  el,
  component: Component,
  props = {},
  customTheme = 'light',
  forMainDocument = false,
  skipUnmount = false,
}: MountOptions) => {
  // 确保初始化
  initReactMount();

  // 清理已存在的 Root
  const existingRoot = rootMap.get(el);
  if (existingRoot) {
    if (!skipUnmount) {
       try {
         existingRoot.unmount();
       } catch (e) {
         console.warn('[mountReactComponent] Unmount failed (benign if DOM was cleared):', e);
       }
    } else {
       // 如果跳过卸载 (用于强制重置)，手动清除 DOM 上的 React 标记
       // 防止 createRoot 警告 "You are calling createRoot() on a container..."
       Object.keys(el).forEach((key) => {
         if (key.startsWith('__reactContainer') || key.startsWith('_reactRootContainer')) {
            try {
              delete (el as any)[key];
            } catch (e) {
              // ignore
            }
         }
       });
    }
    rootMap.delete(el);
  }

  // 创建新的 Root
  const root = createRoot(el);
  rootMap.set(el, root);

  // 样式注入策略：
  // GrapesJS 的 onRender 传入的 el 在渲染时还在主文档中
  // 所以我们使用全局引用来获取 iframe head
  
  // 获取全局 iframe head 引用 (由 UIDesigner.tsx canvas:frame:load 设置)
  const iframeHead = (window as any).__ANTD_CSSINJS_CONTAINER__ as HTMLElement | undefined;
  const iframeDoc = (window as any).__ANTD_IFRAME_DOC__ as Document | undefined;
  
  // 如果指定了 forMainDocument，强制使用主文档
  // 否则，如果 iframe 引用有效，使用 iframe head 和 iframe doc
  const isIframeValid = !forMainDocument && iframeHead && iframeDoc && iframeDoc.defaultView !== null;
  const container = isIframeValid ? iframeHead : window.document.head;
  
  // 关键：使用 iframe document 作为 cache key
  // 这样每个 iframe 有独立的 cache，effectStyles 会是新的
  // 如果 forMainDocument 为 true，使用主文档的 cache
  const cacheDoc = isIframeValid ? iframeDoc : window.document;
  const cache = getOrCreateCache(cacheDoc);



  // 获取全局主题配置
  const globalTheme = (window as any)['__GLS_COMPS__']?.theme;
  const finalTheme = globalTheme || customTheme;
  const algorithm =
    finalTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm;

  // 渲染函数 - 使用 StyleProvider 的 container 属性将样式注入到目标容器
  const renderComponent = (componentProps: Record<string, any>) => (
    <StyleProvider cache={cache} container={container} hashPriority="high">
      <ConfigProvider
        locale={zhCN}
        theme={{ 
          algorithm,
          cssVar: {
            key: 'antd-canvas',  // 唯一标识符
          },
          hashed: false,  // 禁用哈希类名，使用静态 CSS 中的选择器
        }}
        getPopupContainer={() => el}
      >
        <Component {...componentProps} />
      </ConfigProvider>
    </StyleProvider>
  );

  // 设置当前样式容器，让 monkey-patch 知道应该在哪个 document 创建 style 元素
  // 这个全局变量会被 UIDesigner.tsx 中的 createElement patch 检查
  (window as any).__CURRENT_STYLE_CONTAINER__ = container;

  // 渲染组件
  const isEditing = el.hasAttribute('editing');
  const finalProps = { ...props };
  if (isEditing && finalProps.develop === undefined) {
    finalProps.develop = true;
  }
  
  root.render(renderComponent(finalProps));

  // 返回控制接口
  return {
    root,
    unmount: () => {
      try {
        root.unmount();
        rootMap.delete(el);
      } catch (error) {
        console.error('[code-canvas-core] Failed to unmount component:', error);
      }
    },
    update: (newProps: Record<string, any>) => {
      // 更新时重新设置容器
      (window as any).__CURRENT_STYLE_CONTAINER__ = container;
      root.render(renderComponent(newProps));
    },
  };
};

/**
 * 清理所有已挂载的组件
 */
export const cleanupAllMounts = () => {
  console.info('[code-canvas-core] Cleanup called - components will be garbage collected');
  initialized = false;
};

export default mountReactComponent;
